import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BrutalHeader from './BrutalHeader';
import './App.css';
import './LawFirms.css';

export interface ProcessStep {
  num: string;
  icon: string;
  title: string;
  desc: string;
}

export interface ValueProp {
  title: string;
  desc: string;
}

export interface AiAccelPoint {
  title: string;
  desc: string;
}

export interface IndustryConfig {
  slug: string;
  label: string;
  heroTitle: string;
  heroDim: string;
  heroSubtitle: string;
  heroImage: string;
  heroImageAlt: string;
  processSteps: ProcessStep[];
  valueHeading: string;
  valueProps: ValueProp[];
  aiAccelHeading: string;
  aiAccelPoints: AiAccelPoint[];
  ctaHeading: string;
  ctaBody: string;
}

const ALL_INDUSTRIES = [
  { label: 'Law',   href: '/industries/law-firms' },
  { label: 'Healthcare',  href: '/industries/healthcare' },
  { label: 'Real Estate', href: '/industries/real-estate' },
  { label: 'Publishing',  href: '/industries/publishing' },
  { label: 'Service', href: '/industries/services' },
  { label: 'Restaurants', href: '/industries/restaurants' },
  { label: 'Other',       href: '/industries/other' },
];

function IndustryTemplate({ config }: { config: IndustryConfig }) {
  const navigate = useNavigate();
  const hiwRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = hiwRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const activeHref = `/industries/${config.slug}`;

  return (
    <div className="industry-shell">
      <BrutalHeader />

      {/* Hero */}
      <section className="industry-hero">
        <div className="industry-hero-inner">
          <div className="industry-hero-left">
            <span className="industry-eyebrow">{config.label}</span>
            <h1 className="industry-hero-title">
              {config.heroTitle}<br />
              <span className="industry-hero-dim">{config.heroDim}</span>
            </h1>
            <p className="industry-hero-subtitle">{config.heroSubtitle}</p>

            <div className="industry-tabs">
              {ALL_INDUSTRIES.map((ind) => (
                <a
                  key={ind.label}
                  href={ind.href}
                  className={`industry-tab${ind.href === activeHref ? ' industry-tab-active' : ''}`}
                  onClick={ind.href === activeHref ? (e) => e.preventDefault() : undefined}
                >
                  {ind.label}
                </a>
              ))}
            </div>

            <button className="industry-cta-btn" onClick={() => navigate('/signup')}>
              Join our network →
            </button>
          </div>

          <div className="industry-hero-right">
            {config.heroImage && (
              <img src={config.heroImage} alt={config.heroImageAlt} className="industry-hero-img" />
            )}
          </div>
        </div>
      </section>

      {/* How It Works — animated track */}
      <section ref={hiwRef} className="hiw-section">
        <div className="hiw-inner">
          <div className="hiw-header">
            <span className="hiw-label">Our Process</span>
            <h2 className="hiw-title">How It Works</h2>
          </div>

          <div className="hiw-track-wrap">
            <div className="hiw-track-line" />
            <div className="hiw-track-ball" />
            <div className="hiw-nodes-row">
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-1" /></div>
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-2" /></div>
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-3" /></div>
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-4" /></div>
            </div>
          </div>

          <div className="hiw-cards">
            {config.processSteps.map((step, i) => (
              <div key={i} className={`hiw-card hiw-card-${i + 1}`}>
                <img src={step.icon} alt="" className="hiw-card-icon" />
                <span className="hiw-card-num">{step.num}</span>
                <h3 className="hiw-card-title">{step.title}</h3>
                <p className="hiw-card-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="industry-value">
        <div className="industry-value-inner">
          <span className="industry-section-label" style={{ color: 'rgba(255,255,255,0.38)' }}>
            Why It Works
          </span>
          <h2 className="industry-section-title" style={{ color: '#fff' }}>
            {config.valueHeading}
          </h2>
          <div className="industry-value-grid">
            {config.valueProps.map((vp, i) => (
              <div key={i} className="industry-value-card">
                <h3 className="industry-value-card-title">{vp.title}</h3>
                <p className="industry-value-card-desc">{vp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Acceleration */}
      <section className="accel-section">
        <div className="accel-inner">
          <div className="accel-left">
            <span className="accel-eyebrow">The Partnership Advantage</span>
            <h2 className="accel-heading">{config.aiAccelHeading}</h2>
            <p className="accel-body">
              Data monetization is just the start. As a partner, you get direct access to our team
              to help you apply AI across your operations — turning industry-wide insights into a
              competitive edge for your business.
            </p>
          </div>
          <div className="accel-right">
            {config.aiAccelPoints.map((pt, i) => (
              <div key={i} className="accel-point">
                <h3 className="accel-point-title">{pt.title}</h3>
                <p className="accel-point-desc">{pt.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="industry-cta-section">
        <div className="industry-cta-inner">
          <div className="industry-cta-text">
            <h2>{config.ctaHeading}</h2>
            <p>{config.ctaBody}</p>
          </div>
          <button className="industry-cta-btn" onClick={() => navigate('/signup')}>
            Join our network →
          </button>
        </div>
      </section>
    </div>
  );
}

export default IndustryTemplate;
